import React from 'react'
import {
  Box,
  Text,
  Button,
  HStack,
  VStack,
  IconButton
} from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'

interface CookiePreferences {
  essential: boolean
  marketing: boolean
}

interface CookieConsentBannerProps {
  onAccept?: (preferences: CookiePreferences) => void
}

const STORAGE_KEY = 'cookie_consent_given'
const PREFS_KEY = 'cookie_preferences'

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onAccept,
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setIsVisible(true)
    }
  }, [])

  const persist = (preferences: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, 'true')
    localStorage.setItem(PREFS_KEY, JSON.stringify(preferences))
    setIsVisible(false)
    onAccept?.(preferences)
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: preferences }))
  }

  const handleEssentialOnly = () => {
    persist({ essential: true, marketing: false })
  }

  const handleAcceptAll = () => {
    persist({ essential: true, marketing: true })
  }

  if (!isVisible) {
    return null
  }

  return (
    <Box
      position="fixed"
      bottom={{ base: 0, sm: 4 }}
      right={{ base: 0, sm: 4 }}
      left={{ base: 0, sm: 'auto' }}
      zIndex={1000}
      maxW={{ base: 'full', sm: '360px' }}
      bg="white"
      borderRadius={{ base: 0, sm: 'md' }}
      borderWidth={{ base: '1px 0 0 0', sm: '1px' }}
      borderColor="gray.200"
      shadow="lg"
      p={4}
      pb={{ base: 'max(1rem, env(safe-area-inset-bottom))', sm: 4 }}
    >
      <VStack align="stretch" gap={3}>
        <HStack justify="space-between" align="start">
          <Text fontSize="sm" lineHeight="tall" display={{ base: 'none', sm: 'block' }}>
            We use essential cookies for sign-in and security. On our public marketing pages only,
            we may use Google advertising cookies to measure ad performance if you opt in. We do
            not use advertising or analytics cookies inside the logged-in app. See our{' '}
            <RouterLink to="/privacy#cookies-tracking">
              <Text as="span" color="blue.600" textDecoration="underline" fontWeight="semibold">
                Cookie Policy
              </Text>
            </RouterLink>
            .
          </Text>
          <Text fontSize="sm" lineHeight="tall" display={{ base: 'block', sm: 'none' }}>
            We use essential cookies, plus optional ad-performance cookies if you opt in. See our{' '}
            <RouterLink to="/privacy#cookies-tracking">
              <Text as="span" color="blue.600" textDecoration="underline" fontWeight="semibold">
                Cookie Policy
              </Text>
            </RouterLink>
            .
          </Text>
          <IconButton
            aria-label="Close cookie banner"
            size="sm"
            variant="ghost"
            onClick={handleEssentialOnly}
          >
            <FiX />
          </IconButton>
        </HStack>

        <HStack gap={2} justify="flex-end">
          <Button size="sm" variant="outline" onClick={handleEssentialOnly}>
            Essential only
          </Button>
          <Button size="sm" colorScheme="blue" onClick={handleAcceptAll}>
            Accept all
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

export default CookieConsentBanner
